// The Maeve mascot face — built entirely in SVG to the exact geometry in
// the Brand & Design Guidelines §5 (Maeve — visual design). It must render
// identically everywhere and must never be replaced with an emoji, generic
// avatar, or third-party illustration.
//
// Two variants (§5 "Maeve face — two variants"):
//   - standard  — manager / HR context. Standard smile, no cheeks, teal
//                 glow rings.
//   - warm      — employee / companion context. Wider smile, visible
//                 cheeks, blue-tinted glow rings.
//
// Anti-anthropomorphism (DESIGN §1.4 #8) forbids first-person Maeve speech
// and relationship framing but permits this decorative mark. CGC review of
// the asset itself is an M3 prerequisite (DESIGN §6.2.1).
//
// `aria-hidden` because the face is decorative — meaning lives in the
// adjacent copy. No `alt` text that implies an interlocutor.

interface MaeveAvatarProps {
  /** Rendered pixel size. Ambient rings (when on) extend beyond the face. */
  size?: number;
  /** Render the soft ambient glow rings around the face. Off in chat headers. */
  rings?: boolean;
  /** Visual variant — `warm` for employee/companion, `standard` elsewhere. */
  variant?: 'standard' | 'warm';
  /**
   * Add a thin tinted edge stroke to the face circle. The governed fills
   * (Deep Navy outer / Ink inner, §5 face construction) sit almost exactly
   * on top of the app's own dark-navy chrome (`bg-ink`) — on a dark
   * background the face is otherwise only visible via the eyes/smile, with
   * no perceivable boundary. This is purely an additive stroke; it does not
   * change the fills the "must render identically everywhere" rule governs.
   */
  onDark?: boolean;
  className?: string;
}

// Glow-ring tints (§5 Maeve glow rings). Standard = teal; warm = the
// employee blue substitution called out in the spec.
const RING_RGB = {
  standard: '61, 191, 187', // #3DBFBB
  warm: '91, 168, 212', // #5BA8D4
} as const;

export function MaeveAvatar({
  size = 120,
  rings = true,
  variant = 'standard',
  onDark = false,
  className,
}: MaeveAvatarProps) {
  const warm = variant === 'warm';

  // Spec geometry is authored against a 200×200 box with the face centred
  // at (100,100), r=82. When rings are shown we widen the viewBox so the
  // two concentric rings (≈1.4× and ≈1.75× the 164px face diameter) have
  // room to sit behind the face; the face coordinates stay identical.
  const viewBox = rings ? '-44 -44 288 288' : '0 0 200 200';
  const ringRgb = RING_RGB[variant];

  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      className={className}
      // §6 Maeve face glow — only as a hero element (rings on); kept off
      // the small inline/chat instances to avoid a heavy blur per bubble.
      style={rings ? { filter: 'drop-shadow(0 4px 18px rgba(61, 191, 187, 0.2))' } : undefined}
      aria-hidden
      focusable={false}
    >
      {rings ? (
        <>
          <circle
            cx={100}
            cy={100}
            r={115}
            fill="none"
            stroke={`rgba(${ringRgb}, 0.18)`}
            strokeWidth={1}
          />
          <circle
            cx={100}
            cy={100}
            r={143}
            fill="none"
            stroke={`rgba(${ringRgb}, 0.1)`}
            strokeWidth={1}
          />
        </>
      ) : null}

      {/* Face — Deep Navy outer, Ink inner for depth (§5 face construction).
          `onDark` adds a thin tinted stroke on the outer circle only — the
          fills are unchanged; this just gives the boundary visibility it
          otherwise loses against a background painted the same ink color. */}
      <circle
        cx={100}
        cy={100}
        r={82}
        fill="var(--color-navy)"
        stroke={onDark ? `rgba(${ringRgb}, 0.5)` : undefined}
        strokeWidth={onDark ? 1.5 : undefined}
      />
      <circle cx={100} cy={100} r={76} fill="var(--color-maeve-ink)" />

      {/* Cheeks (warm variant only) sit behind the eyes/smile. */}
      {warm ? (
        <g fill="rgba(61, 191, 187, 0.12)">
          <ellipse cx={56} cy={116} rx={11} ry={6} />
          <ellipse cx={144} cy={116} rx={11} ry={6} />
        </g>
      ) : null}

      {/* Eyes — teal disc, ink pupil, white highlight (both variants). */}
      <g fill="var(--color-maeve-glow)">
        <circle cx={72} cy={88} r={11} />
        <circle cx={128} cy={88} r={11} />
      </g>
      <g fill="var(--color-maeve-ink)">
        <circle cx={72} cy={86} r={5} />
        <circle cx={128} cy={86} r={5} />
      </g>
      <g fill="#ffffff" fillOpacity={0.9}>
        <circle cx={75} cy={83} r={2} />
        <circle cx={131} cy={83} r={2} />
      </g>

      {/* Smile — wider on the warm variant. */}
      <path
        d={warm ? 'M62 122 Q100 148 138 122' : 'M68 118 Q100 140 132 118'}
        stroke="var(--color-maeve-glow)"
        strokeWidth={warm ? 3.5 : 3}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
